import {
  Button,
  InputAdornment,
  Slider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { FLAG_DISABLE_SSR_WARNING, theme } from "portal/theme/theme";
import { isUndefined } from "portal/utils/identity";
import { makeStyles } from "tss-react/mui";
import { numberOrEmptyString } from "portal/utils/forms";
import { useTranslation } from "react-i18next";
import Color from "color";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import PasteIcon from "@mui/icons-material/ContentPasteOutlined";
import React, { CSSProperties, FunctionComponent, useMemo } from "react";

interface Props {
  copiedSizes: number[] | undefined;
  onChange: (sizes: number[]) => void;
  setCopiedSizes: (sizes: number[] | undefined) => void;
  sizes: number[];
  readOnly?: boolean;
}

interface ThresholdColors {
  small: string;
  medium: string;
  large: string;
}

const useStyles = makeStyles<ThresholdColors>()(
  (_theme, { small, medium, large }) => ({
    root: {
      // Color the rail in three segments matching small / medium / large.
      // Thumb positions are CSS vars so they update while dragging without
      // regenerating Emotion classes.
      [`& .MuiSlider-rail ${FLAG_DISABLE_SSR_WARNING}`]: {
        opacity: 1,
        background: `linear-gradient(
        to right,
        ${small} 0%,
        ${small} var(--threshold-thumb1),
        ${medium} var(--threshold-thumb1),
        ${medium} var(--threshold-thumb2),
        ${large} var(--threshold-thumb2),
        ${large} 100%
      )`,
      },
      "&>*": {
        // Small range: first thumb, Small label, first threshold value
        [`&:nth-child(1 of .MuiSlider-thumb), &:nth-child(2 of .MuiSlider-markLabel), &:nth-child(3 of .MuiSlider-markLabel) ${FLAG_DISABLE_SSR_WARNING}`]:
          {
            color: `${small} !important`,
          },
        [`&:nth-child(1 of .MuiSlider-thumb):hover ${FLAG_DISABLE_SSR_WARNING}`]:
          {
            boxShadow: `0px 0px 0px 8px ${new Color(small)
              .alpha(0.16)
              .rgb()} !important`,
          },

        // Medium range: second thumb, Medium label, second threshold value
        [`&:nth-child(2 of .MuiSlider-thumb), &:nth-child(4 of .MuiSlider-markLabel), &:nth-child(5 of .MuiSlider-markLabel) ${FLAG_DISABLE_SSR_WARNING}`]:
          {
            color: `${medium} !important`,
          },
        [`&:nth-child(2 of .MuiSlider-thumb):hover ${FLAG_DISABLE_SSR_WARNING}`]:
          {
            boxShadow: `0px 0px 0px 8px ${new Color(medium)
              .alpha(0.16)
              .rgb()} !important`,
          },

        // Large range label
        [`&:nth-child(6 of .MuiSlider-markLabel) ${FLAG_DISABLE_SSR_WARNING}`]:
          {
            color: `${large} !important`,
          },

        // move size labels above rail
        [`&:nth-child(2n of .MuiSlider-markLabel) ${FLAG_DISABLE_SSR_WARNING}`]:
          {
            marginTop: -50,
          },
      },
    },
    smallField: {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: small,
        borderWidth: 2,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: small,
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: small,
      },
    },
    mediumField: {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: medium,
        borderWidth: 2,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: medium,
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: medium,
      },
    },
  })
);

export const CategoryThresholds: FunctionComponent<Props> = ({
  copiedSizes,
  onChange,
  setCopiedSizes,
  sizes,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const {
    palette: {
      chart: { bandStrokeColors },
    },
  } = useTheme();
  // Match shoot power chart bands; fall back to the previous slider theme colors.
  const colors = useMemo(
    (): ThresholdColors => ({
      small: new Color(bandStrokeColors[0] ?? theme.colors.violet["500"]).hex(),
      medium: new Color(bandStrokeColors[1] ?? theme.colors.teal["500"]).hex(),
      large: new Color(bandStrokeColors[2] ?? theme.colors.yellow["600"]).hex(),
    }),
    [bandStrokeColors]
  );

  // track the state for these two fields locally because we need to allow them
  // to be empty string to allow the user to delete the value but we don't want
  // to propagate that invalid state to the parent
  const [thumb1, setThumb1] = React.useState<number | "">(sizes[0] ?? "");
  const [thumb2, setThumb2] = React.useState<number | "">(sizes[1] ?? "");

  const thumb1DisplayValue = thumb1 || 0;
  const thumb2DisplayValue = thumb2 || 100;

  const { classes: sliderClasses } = useStyles(colors);

  const sliderStyle = {
    "--threshold-thumb1": `${thumb1DisplayValue}%`,
    "--threshold-thumb2": `${thumb2DisplayValue}%`,
  } as CSSProperties;

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 w-full gap-4">
        <Typography variant="h4" className="text-xl">
          {t("components.almanac.formulas.thresholds")}
        </Typography>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CopyIcon />}
                onClick={() => setCopiedSizes(sizes)}
                disabled={readOnly}
              >
                {t("components.almanac.formulas.copySize")}
              </Button>
              {copiedSizes && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PasteIcon />}
                  onClick={() => onChange([...copiedSizes])}
                >
                  {t("components.almanac.formulas.pasteSize")}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {/* Text entry */}
      <div className="flex justify-evenly items-center">
        <TextField
          size="small"
          variant="outlined"
          type="number"
          className={`w-28 ${sliderClasses.smallField}`}
          disabled={readOnly}
          value={thumb1}
          onChange={(event) => {
            const value = numberOrEmptyString(event.currentTarget.value);
            setThumb1(value);
            if (typeof value === "number" && typeof thumb2 === "number") {
              onChange([value, thumb2]);
            }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {t("utils.units.mm")}
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          size="small"
          variant="outlined"
          type="number"
          className={`w-28 ${sliderClasses.mediumField}`}
          disabled={readOnly}
          value={sizes[1]}
          onChange={(event) => {
            const value = numberOrEmptyString(event.currentTarget.value);
            setThumb2(value);
            if (typeof value === "number" && typeof thumb1 === "number") {
              onChange([thumb1, value]);
            }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {t("utils.units.mm")}
                </InputAdornment>
              ),
            },
          }}
        />
      </div>
      {/* Slider */}
      <Slider
        track={false}
        min={0}
        max={100}
        disabled={readOnly}
        className={sliderClasses.root}
        style={sliderStyle}
        classes={{
          root: "my-10",
          mark: "hidden",
        }}
        value={[thumb1DisplayValue, thumb2DisplayValue]}
        onChange={(event, value) => {
          if (typeof value === "number") {
            return;
          }
          const [thumb1Value, thumb2Value] = value;
          if (isUndefined(thumb1Value) || isUndefined(thumb2Value)) {
            return;
          }
          setThumb1(thumb1Value);
          setThumb2(thumb2Value);
          if (
            typeof thumb1Value === "number" &&
            typeof thumb2Value === "number"
          ) {
            onChange([thumb1Value, thumb2Value]);
          }
        }}
        marks={[
          { value: 0, label: `0${t("utils.units.mm")}` },
          {
            value: (thumb1 || 0) / 2,
            label: t("utils.descriptors.small"),
          },
          {
            value: thumb1DisplayValue,
            label: `${thumb1DisplayValue}${t("utils.units.mm")}`,
          },
          {
            value:
              thumb1DisplayValue +
              (thumb2DisplayValue - thumb1DisplayValue) / 2,
            label: t("utils.descriptors.medium"),
          },
          {
            value: thumb2DisplayValue,
            label: `${thumb2DisplayValue}${t("utils.units.mm")}`,
          },
          {
            value: thumb2DisplayValue + (100 - thumb2DisplayValue) / 2,
            label: t("utils.descriptors.large"),
          },
          { value: 100, label: `100${t("utils.units.mm")}` },
        ]}
      />
    </>
  );
};
